import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/api";
import { Sparkles, Save, Send, ArrowLeft, Loader2, Plus, X } from "lucide-react";

const PROF_TAGS = [
  ["avocat", "Avocat"],
  ["expert-comptable", "Expert-comptable"],
  ["notaire", "Notaire"],
];

const STATUS_LABEL = {
  draft: "Brouillon",
  pending: "En attente de relecture",
  published: "Publié",
  rejected: "Refusé",
};

const STATUS_BADGE = {
  draft: "bg-slate-100 text-slate-700",
  pending: "bg-amber-100 text-amber-800",
  published: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
};

export default function ArticleEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [article, setArticle] = useState({
    id: null,
    status: "draft",
    rejection_reason: null,
    title: "",
    excerpt: "",
    body: "",
    cover_image: "",
    profession_tags: [],
    seo_meta_title: "",
    seo_meta_description: "",
    seo_keywords: [],
    faq: [],
  });
  const [keywordsInput, setKeywordsInput] = useState("");

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    apiClient.get("/articles/mine")
      .then(r => {
        const found = (r.data || []).find(a => a.id === id);
        if (found) {
          // Assign stable client-side ids to FAQ items so they survive add/remove without losing focus
          const faqWithIds = (found.faq || []).map(f => ({ ...f, _uid: crypto.randomUUID() }));
          setArticle({ ...article, ...found, faq: faqWithIds });
          setKeywordsInput((found.seo_keywords || []).join(", "));
        } else {
          toast.error("Article introuvable");
          nav("/expert/dashboard");
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const update = (k, v) => setArticle(prev => ({ ...prev, [k]: v }));
  const toggleTag = (t) => {
    setArticle(prev => ({
      ...prev,
      profession_tags: prev.profession_tags.includes(t)
        ? prev.profession_tags.filter(x => x !== t)
        : [...prev.profession_tags, t],
    }));
  };

  const buildPayload = () => ({
    title: article.title,
    excerpt: article.excerpt || null,
    body: article.body,
    cover_image: article.cover_image || null,
    profession_tags: article.profession_tags,
    category_ids: [],
    city_tags: [],
    problem_tags: [],
    seo_meta_title: article.seo_meta_title || null,
    seo_meta_description: article.seo_meta_description || null,
    seo_keywords: keywordsInput.split(",").map(s => s.trim()).filter(Boolean),
    seo_og_image: article.cover_image || null,
    faq: (article.faq || [])
      .filter(f => f.q && f.a)
      .map(({ q, a }) => ({ q, a })),
    cosigners: [],
  });

  const validate = () => {
    if ((article.title || "").length < 8) { toast.error("Le titre doit contenir au moins 8 caractères"); return false; }
    if ((article.body || "").length < 80) { toast.error("Le corps de l'article est trop court (80 caractères minimum)"); return false; }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      let res;
      if (article.id) {
        res = await apiClient.patch(`/articles/${article.id}`, payload);
      } else {
        res = await apiClient.post("/articles", payload);
      }
      setArticle(prev => ({ ...prev, ...res.data }));
      toast.success("Article enregistré");
      if (!article.id && res.data?.id) {
        nav(`/expert/articles/${res.data.id}`, { replace: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const submitForReview = async () => {
    if (!article.id) { toast.error("Enregistrez d'abord votre article"); return; }
    setSubmitting(true);
    try {
      await apiClient.post(`/articles/${article.id}/submit`);
      setArticle(prev => ({ ...prev, status: "pending", rejection_reason: null }));
      toast.success("Article transmis au comité éditorial");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la soumission");
    } finally {
      setSubmitting(false);
    }
  };

  const aiSeoAssist = async () => {
    if ((article.body || "").length < 200) {
      toast.error("Rédigez au moins 200 caractères avant l'analyse IA");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await apiClient.post("/articles/ai/seo", {
        title: article.title,
        body: article.body,
        profession_tags: article.profession_tags,
      });
      setArticle(prev => ({
        ...prev,
        seo_meta_title: data.meta_title || prev.seo_meta_title,
        seo_meta_description: data.meta_description || prev.seo_meta_description,
        excerpt: prev.excerpt || data.excerpt || "",
        faq: (prev.faq?.length
          ? prev.faq
          : (data.suggested_faq || []).map(f => ({ ...f, _uid: crypto.randomUUID() }))),
      }));
      setKeywordsInput((data.keywords || []).join(", "));
      toast.success("Suggestions SEO générées");
    } catch (e) {
      toast.error("L'analyse IA a échoué");
    } finally {
      setAiLoading(false);
    }
  };

  const addFaq = () => setArticle(p => ({
    ...p,
    faq: [...(p.faq || []), { q: "", a: "", _uid: crypto.randomUUID() }],
  }));
  const updateFaq = (i, k, v) => setArticle(p => ({
    ...p, faq: p.faq.map((f, idx) => idx === i ? { ...f, [k]: v } : f),
  }));
  const removeFaq = (i) => setArticle(p => ({ ...p, faq: p.faq.filter((_, idx) => idx !== i) }));

  if (loading) {
    return (
      <div className="bg-[#FAFAF7] min-h-screen">
        <Navbar variant="light" />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center text-slate-500">Chargement…</div>
      </div>
    );
  }

  const editable = article.status === "draft" || article.status === "rejected" || !article.id;

  return (
    <div className="bg-[#FAFAF7] min-h-screen">
      <Seo title="Rédaction d'article" noindex />
      <Navbar variant="light" />

      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/expert/dashboard" className="text-sm text-slate-500 hover:text-[#0B3A2A] inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour à l'espace personnel
        </Link>

        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-2">Cahier éditorial</div>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight text-[#0B3A2A]">
              {article.id ? "Édition d'article" : "Nouvel article"}
            </h1>
          </div>
          <Badge className={`rounded-full ${STATUS_BADGE[article.status]}`}>
            {STATUS_LABEL[article.status]}
          </Badge>
        </div>

        {article.status === "rejected" && article.rejection_reason && (
          <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-xl p-4 mb-6 text-sm">
            <strong>Motif du retour éditorial : </strong>{article.rejection_reason}
          </div>
        )}
        {article.status === "pending" && (
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-4 mb-6 text-sm">
            Cet article est en attente de relecture par le comité éditorial.
          </div>
        )}

        <fieldset disabled={!editable} className="space-y-6">
          {/* Title */}
          <Field label="Titre" required>
            <Input
              value={article.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="Titre institutionnel, descriptif et précis"
              data-testid="article-title-input"
              className="h-12 rounded-xl border-slate-200 bg-white"
            />
          </Field>

          {/* Profession tags */}
          <Field label="Domaines concernés">
            <div className="flex flex-wrap gap-2">
              {PROF_TAGS.map(([k, label]) => {
                const active = article.profession_tags.includes(k);
                return (
                  <button
                    type="button"
                    key={k}
                    onClick={() => toggleTag(k)}
                    data-testid={`tag-${k}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-smooth ${
                      active
                        ? "bg-[#0B3A2A] text-white border-[#0B3A2A]"
                        : "bg-white text-slate-700 border-slate-200 hover:border-[#C9A961]/40"
                    }`}
                  >{label}</button>
                );
              })}
            </div>
          </Field>

          {/* Cover image */}
          <Field label="URL de la photo de couverture">
            <Input
              value={article.cover_image}
              onChange={(e) => update("cover_image", e.target.value)}
              placeholder="https://…"
              data-testid="article-cover-input"
              className="h-11 rounded-xl border-slate-200 bg-white"
            />
            {article.cover_image && (
              <img src={article.cover_image} alt="" className="mt-3 rounded-xl max-h-48 border border-slate-100" />
            )}
          </Field>

          {/* Excerpt */}
          <Field label="Chapeau / Excerpt" hint="1 à 2 phrases sobres pour les listes et le SEO.">
            <Textarea
              value={article.excerpt}
              onChange={(e) => update("excerpt", e.target.value)}
              data-testid="article-excerpt-input"
              rows={2}
              className="rounded-xl border-slate-200 bg-white"
            />
          </Field>

          {/* Body */}
          <Field label="Corps de l'article" required hint="Markdown léger pris en charge : `## Sous-titre`, `**gras**`.">
            <Textarea
              value={article.body}
              onChange={(e) => update("body", e.target.value)}
              data-testid="article-body-input"
              rows={18}
              className="rounded-xl border-slate-200 bg-white font-mono text-[13.5px] leading-relaxed"
              placeholder="## Une démarche pluridisciplinaire&#10;&#10;Texte de l'article…"
            />
            <div className="text-xs text-slate-500 mt-1.5">{(article.body || "").length} caractères</div>
          </Field>

          {/* AI assist */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#C9A961]" />
                <h3 className="font-serif text-lg text-[#0B3A2A]">Optimisation SEO assistée</h3>
              </div>
              <Button
                onClick={aiSeoAssist}
                disabled={aiLoading || !editable}
                data-testid="article-ai-seo"
                variant="outline"
                size="sm"
                className="rounded-full border-[#C9A961] text-[#8A6E3A] hover:bg-[#C9A961]/10"
              >
                {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Générer méta + FAQ
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Claude Sonnet 4.5 propose un titre SEO, une méta description, des mots-clés et une FAQ. Vous restez libre de valider et d'éditer chaque champ.
            </p>
          </div>

          <Field label="Méta title (50–65 caractères)">
            <Input
              value={article.seo_meta_title}
              onChange={(e) => update("seo_meta_title", e.target.value)}
              data-testid="article-meta-title"
              className="h-11 rounded-xl border-slate-200 bg-white"
            />
          </Field>
          <Field label="Méta description (140–160 caractères)">
            <Textarea
              value={article.seo_meta_description}
              onChange={(e) => update("seo_meta_description", e.target.value)}
              data-testid="article-meta-desc"
              rows={2}
              className="rounded-xl border-slate-200 bg-white"
            />
          </Field>
          <Field label="Mots-clés" hint="Séparés par une virgule.">
            <Input
              value={keywordsInput}
              onChange={(e) => setKeywordsInput(e.target.value)}
              data-testid="article-keywords"
              className="h-11 rounded-xl border-slate-200 bg-white"
            />
          </Field>

          {/* FAQ */}
          <Field label="Questions fréquentes" hint="Apparaîtront en bas d'article (rich snippet FAQ).">
            <div className="space-y-3">
              {(article.faq || []).map((f, i) => (
                <div key={f._uid || i} className="bg-white border border-slate-200 rounded-xl p-4 relative">
                  <button
                    type="button"
                    onClick={() => removeFaq(i)}
                    className="absolute top-3 right-3 text-slate-400 hover:text-rose-600"
                    data-testid={`faq-remove-${i}`}
                  ><X className="w-4 h-4" /></button>
                  <Input
                    value={f.q}
                    onChange={(e) => updateFaq(i, "q", e.target.value)}
                    placeholder="Question"
                    className="h-10 rounded-lg border-slate-200 mb-2"
                    data-testid={`faq-q-${i}`}
                  />
                  <Textarea
                    value={f.a}
                    onChange={(e) => updateFaq(i, "a", e.target.value)}
                    rows={2}
                    placeholder="Réponse sobre"
                    className="rounded-lg border-slate-200"
                    data-testid={`faq-a-${i}`}
                  />
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addFaq} className="rounded-full" data-testid="faq-add">
                <Plus className="w-4 h-4 mr-2" /> Ajouter une question
              </Button>
            </div>
          </Field>
        </fieldset>

        {/* Actions */}
        <div className="mt-10 flex flex-col sm:flex-row gap-3 sticky bottom-4 bg-white/80 backdrop-blur-md p-4 rounded-2xl border border-slate-200 shadow-sm">
          <Button
            onClick={save}
            disabled={!editable || saving}
            data-testid="article-save"
            variant="outline"
            className="rounded-full flex-1"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
          <Button
            onClick={submitForReview}
            disabled={!article.id || !editable || submitting}
            data-testid="article-submit"
            className="rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold flex-1"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Soumettre pour relecture
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#0B3A2A] mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-500 mt-1.5">{hint}</p>}
    </div>
  );
}
